import axios from "axios";
import { attachAuthInterceptor } from "./authInterceptor";
import { attachReadScope, NO_SCOPE } from "./requestScope";
import { absorbAllDataFields } from "./fieldPayload";

const API_BASE_URL = (window.__RUNTIME_CONFIG__ || {}).REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach shared auth interceptor (token injection + 401 refresh + retry)
attachAuthInterceptor(api);

// Attach the editor read scope: GETs issued through this instance are bound to
// the current navigation scope and cancelled when the user changes editor tab.
// Only this instance is scoped — the monitoring/AI modules create their own
// axios clients, so their pollers are never touched by an editor tab click.
attachReadScope(api);

// Orgs are static for the session and were being refetched on mount from
// 16+ components. Module-level promise cache dedupes concurrent callers and
// reuses the result across route transitions. Mutations call
// invalidateOrgsCache().
let _orgsCachePromise = null;
export const fetchOrgs = async () => {
  if (!_orgsCachePromise) {
    _orgsCachePromise = api
      .get("/orgs", NO_SCOPE)
      .then((r) => r.data)
      .catch((err) => {
        _orgsCachePromise = null;
        throw err;
      });
  }
  return _orgsCachePromise;
};
export const invalidateOrgsCache = () => {
  _orgsCachePromise = null;
};

// GET /auth/me — profile + target-mode capability ("" builder, "consumer").
// Session-static like orgs; same cache pattern.
let _meCachePromise = null;
export const fetchMe = async () => {
  if (!_meCachePromise) {
    _meCachePromise = api
      .get("/auth/me", NO_SCOPE)
      .then((r) => r.data)
      .catch((err) => {
        _meCachePromise = null;
        throw err;
      });
  }
  return _meCachePromise;
};
// Drop the cached /auth/me so the next fetchMe() re-fetches. Called on login
// and logout so a new session gets its own target-mode capability.
export const invalidateMeCache = () => {
  _meCachePromise = null;
};

export const deleteOrganization = async (orgId) => {
  invalidateOrgsCache();
  const response = await api.delete(`/orgs/${orgId}`);
  return response.data;
};

export const fetchApp = async (appId) => {
  const response = await api.get(`/apps/${appId}`);
  return response.data;
};

export const fetchApps = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/apps`);
  return response.data;
};

// Module-level promise cache keyed by orgId. Collapses concurrent callers
// (e.g. two monitoring panels mounted at the same time) into one request and
// reuses the resolved list across route transitions. Mutations (createApp,
// deleteApp) call invalidateAppsCache to force a fresh fetch next time.
const _appsCache = new Map(); // orgId -> promise
export const fetchAppsCached = (orgId) => {
  if (!_appsCache.has(orgId)) {
    _appsCache.set(
      orgId,
      api.get(`/orgs/${orgId}/apps`, NO_SCOPE).then((r) => r.data).catch((err) => {
        _appsCache.delete(orgId);
        throw err;
      })
    );
  }
  return _appsCache.get(orgId);
};
export const invalidateAppsCache = (orgId) => {
  if (orgId == null) _appsCache.clear();
  else _appsCache.delete(orgId);
};

// Paginated/searchable/sortable apps list for the org dashboard. Hits the
// SAME endpoint as fetchApps but with query params, so the backend returns
// the { apps, total, page, limit } envelope instead of a plain array. Each
// app row includes active_branch_count (no per-app /branches fan-out).
// sort: 'alpha' (default) | 'created'.
export const fetchAppsPaged = async (orgId, { page = 1, limit = 15, search = '', sort = 'alpha' } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  params.set('sort', sort);
  if (search && search.trim()) params.set('search', search.trim());
  const response = await api.get(`/orgs/${orgId}/apps?${params.toString()}`);
  return response.data; // { apps, total, page, limit }
};

export const createOrganization = async (data) => {
  invalidateOrgsCache();
  const response = await api.post(`/orgs`, data);
  return response.data;
};

export const updateOrganization = async (orgId, data) => {
  invalidateOrgsCache();
  const response = await api.put(`/orgs/${orgId}`, data);
  return response.data;
};

export const createApp = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/apps`, data);
  invalidateAppsCache(orgId);
  return response.data;
};

export const deleteApp = async (orgId, appId) => {
  const response = await api.delete(`/orgs/${orgId}/apps/${appId}`);
  invalidateAppsCache(orgId);
  return response.data;
};

export const updateApp = async (appId, data) => {
  const response = await api.put(`/apps/${appId}`, data);
  invalidateAppsCache(); // appId-only signature → clear all orgs' cached lists
  return response.data;
};

// cloneApp duplicates a deployed app's appconfig snapshot to a new app row.
// Body: { name, is_admin? } — name required; is_admin defaults true on the
// backend so the caller administers the cloned app.
export const cloneApp = async (orgId, appId, data) => {
  const response = await api.post(`/orgs/${orgId}/apps/${appId}/clone`, data);
  return response.data;
};

// Agents
export const fetchAgents = async (orgId) => {
  const response = await api.get(`/agents?org_id=${orgId}`);
  return response.data;
};

// Users — global CRUD against tbl_users (no org scoping; org assignment
// is done separately via importUserToOrg / removeUserFromOrg below).
export const fetchUsers = async (params) => {
  const response = await api.get("/users", params ? { params } : undefined);
  return response.data;
};

export const createUser = async (data) => {
  const response = await api.post("/users", data);
  return response.data;
};

export const updateUser = async (userId, data) => {
  const response = await api.put(`/users/${userId}`, data);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

// Assignments & Users (alias for fetchUsers)
export const fetchAllUsers = fetchUsers;

// Org membership list. Called two ways, and the backend answers in kind:
//   fetchOrgUsers(orgId)                     → bare array of every member
//   fetchOrgUsers(orgId, { page, limit })    → { data, total, page, limit }
// `search` matches name or email server-side and works with either shape.
// Callers that only need one page must pass page/limit — omitting them reads
// the whole org.
export const fetchOrgUsers = async (orgId, params) => {
  const response = await api.get(`/orgs/${orgId}/users`, params ? { params } : undefined);
  return response.data;
};

export const importUserToOrg = async (orgId, userId) => {
  const response = await api.post(`/orgs/${orgId}/users/import`, { user_id: userId });
  return response.data;
};

export const removeUserFromOrg = async (orgId, userId) => {
  const response = await api.delete(`/orgs/${orgId}/users/${userId}`);
  return response.data;
};

export const fetchUserAssignments = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/users/${userId}/assignments`);
  return response.data;
};

export const assignApp = async (orgId, userId, appId, isAdmin = false) => {
  const response = await api.post(`/orgs/${orgId}/users/${userId}/apps/${appId}`, { is_admin: isAdmin });
  return response.data;
};

export const unassignApp = async (orgId, userId, appId) => {
  const response = await api.delete(`/orgs/${orgId}/users/${userId}/apps/${appId}`);
  return response.data;
};

export const updateAppAssignment = async (orgId, userId, appId, isAdmin) => {
  const response = await api.patch(`/orgs/${orgId}/users/${userId}/apps/${appId}`, { is_admin: isAdmin });
  return response.data;
};

export const assignAgent = async (orgId, userId, agentId) => {
  const response = await api.post(`/orgs/${orgId}/users/${userId}/agents/${agentId}`);
  return response.data;
};

export const unassignAgent = async (orgId, userId, agentId) => {
  const response = await api.delete(`/orgs/${orgId}/users/${userId}/agents/${agentId}`);
  return response.data;
};

// User Roles & Groups
export const fetchUserRoles = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/users/${userId}/roles`);
  return response.data;
};

export const setUserRoles = async (orgId, userId, roleIds) => {
  const response = await api.put(`/orgs/${orgId}/users/${userId}/roles`, { role_ids: roleIds });
  return response.data;
};

export const fetchUserGroups = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/users/${userId}/groups`);
  return response.data;
};

export const setUserGroups = async (orgId, userId, groupIds) => {
  const response = await api.put(`/orgs/${orgId}/users/${userId}/groups`, { group_ids: groupIds });
  return response.data;
};

// Roles
export const fetchRoles = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/roles`);
  return response.data;
};

export const createRole = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/roles`, data);
  return response.data;
};

export const updateRole = async (orgId, roleId, data) => {
  const response = await api.put(`/orgs/${orgId}/roles/${roleId}`, data);
  return response.data;
};

export const deleteRole = async (orgId, roleId) => {
  const response = await api.delete(`/orgs/${orgId}/roles/${roleId}`);
  return response.data;
};

// fetchRoleImpact — preview of what will be affected by deleting this role.
// The delete is ON DELETE CASCADE at the FK level; this call lets the UI show
// the blast radius before confirming.
export const fetchRoleImpact = async (orgId, roleId) => {
  const response = await api.get(`/orgs/${orgId}/roles/${roleId}/impact`);
  return response.data;
};

// Groups
export const fetchGroups = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/groups`);
  return response.data;
};

export const createGroup = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/groups`, data);
  return response.data;
};

export const updateGroup = async (orgId, groupId, data) => {
  // `data` was historically just a name string; accept both shapes for compatibility.
  const payload = typeof data === 'string' ? { name: data } : data;
  const response = await api.put(`/orgs/${orgId}/groups/${groupId}`, payload);
  return response.data;
};

export const deleteGroup = async (orgId, groupId) => {
  const response = await api.delete(`/orgs/${orgId}/groups/${groupId}`);
  return response.data;
};

// fetchGroupImpact — same shape as fetchRoleImpact; see note there.
export const fetchGroupImpact = async (orgId, groupId) => {
  const response = await api.get(`/orgs/${orgId}/groups/${groupId}/impact`);
  return response.data;
};

// Group Membership
export const fetchGroupUsers = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/users`);
  return response.data;
};

export const addUserToGroup = async (groupId, userId) => {
  const response = await api.post(`/groups/${groupId}/users`, {
    user_id: userId,
  });
  return response.data;
};

export const removeUserFromGroup = async (groupId, userId) => {
  const response = await api.delete(`/groups/${groupId}/users/${userId}`);
  return response.data;
};

// AI Employees
export const fetchAIEmployees = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees`);
  return response.data;
};

export const createAIEmployee = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees`, data);
  return response.data;
};

export const updateAIEmployee = async (orgId, id, data) => {
  const response = await api.put(`/orgs/${orgId}/ai-employees/${id}`, data);
  return response.data;
};

export const deleteAIEmployee = async (orgId, id) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}`);
  return response.data;
};

export const fetchAIEmployeeRoles = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees/${id}/roles`);
  return response.data;
};

// fetchOrgAIDecisions — recent AI-employee decisions, served by studio-backend
// rather than services/aiEmployeeApi.js. That module targets
// REACT_APP_AI_EMPLOYEE_URL (the standalone ai-employee service), so anything
// built on it goes dark when only the studio stack is running; this route is
// registered in the monolith (internal/knowledge/routes.go) and is always up.
//
// `employeeId` matches aiemployee.tbl_ai_decisions.ai_user_id, which is the
// numeric usersystem user id as a string — pass person.user_id, NOT the
// ai_employee_id from the registry.
//
// The knowledge routes are gated on apps_read, not monitoring_read, so a
// monitoring-only user gets a 403 here. Callers should tolerate that rather
// than surfacing it as a hard error.
export const fetchOrgAIDecisions = async (orgId, { employeeId, limit = 10, page = 1 } = {}) => {
  const params = { limit, page };
  if (employeeId) params.employee_id = String(employeeId);
  const response = await api.get(`/orgs/${orgId}/ai-employees/decisions`, { params });
  return response.data;
};

export const setAIEmployeeRoles = async (orgId, id, roleIds) => {
  const response = await api.put(`/orgs/${orgId}/ai-employees/${id}/roles`, { role_ids: roleIds });
  return response.data;
};

export const fetchAIEmployeeGroups = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees/${id}/groups`);
  return response.data;
};

export const setAIEmployeeGroups = async (orgId, id, groupIds) => {
  const response = await api.put(`/orgs/${orgId}/ai-employees/${id}/groups`, { group_ids: groupIds });
  return response.data;
};

export const fetchAIEmployeeAssignments = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/ai-employees/${id}/assignments`);
  return response.data;
};

export const assignAIEmployeeApp = async (orgId, id, appId) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees/${id}/apps/${appId}`);
  return response.data;
};

export const unassignAIEmployeeApp = async (orgId, id, appId) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}/apps/${appId}`);
  return response.data;
};

export const assignAIEmployeeAgent = async (orgId, id, agentId) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees/${id}/agents/${agentId}`);
  return response.data;
};

export const unassignAIEmployeeAgent = async (orgId, id, agentId) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}/agents/${agentId}`);
  return response.data;
};

// Generates a fresh per-employee webhook token used by external callers
// (e.g. agentic-tool-server posting Vapi callbacks) to authenticate against
// the renderer's POST /api/employees/:id/notify endpoint.
// Returns { webhook_token, note } — the token is shown ONCE and cannot be
// retrieved again, only regenerated.
export const generateAIEmployeeWebhookToken = async (orgId, id) => {
  const response = await api.post(`/orgs/${orgId}/ai-employees/${id}/webhook-token`);
  return response.data;
};

// Revokes the per-employee webhook token. After this, external notify
// requests for the employee will be rejected with 401 until a new token
// is generated.
export const revokeAIEmployeeWebhookToken = async (orgId, id) => {
  const response = await api.delete(`/orgs/${orgId}/ai-employees/${id}/webhook-token`);
  return response.data;
};

export const deployApp = async (appId) => {
  const response = await api.post(`/dashboard/apps/${appId}/deploy`, {});
  return response.data;
};

// streamDeployApp lives in ./deployApi.js and is re-exported via the
// `export * from "./deployApi"` barrel at the bottom of this file. A stale
// local copy used to live here that only forwarded event.label on errors —
// dropping event.detail meant lint failures showed up as "Linting app"
// instead of the actual sentences. ESM-export shadowing made the local copy
// win over the re-export, masking the deployApi.js fix. Removed; do NOT
// reintroduce a local streamDeployApp here.

export const fetchDeployPreview = async (appId) => {
  const response = await api.get(`/dashboard/apps/${appId}/deploy/preview`);
  return response.data;
};

// streamStartApp is also re-exported from ./deployApi.js — same
// label-vs-detail bug as streamDeployApp lived here. Removed for the same
// reason. Do NOT reintroduce a local streamStartApp here.

export const stopApp = async (appId) => {
  const response = await api.post(`/dashboard/apps/${appId}/stop`);
  return response.data;
};

export const setRuntimeTier = async (appId, tier) => {
  const response = await api.put(`/dashboard/apps/${appId}/runtime-tier`, { tier });
  return response.data;
};

export const fetchRuntimeStatus = async (appId) => {
  const response = await api.get(`/apps/${appId}/runtime-status`);
  return response.data;
};

export const fetchDeploymentHistory = async (appId, page = 1, limit = 10) => {
  const response = await api.get(`/dashboard/apps/${appId}/deployments`, { params: { page, limit } });
  return response.data;
};

export const fetchRecordViewsByApp = async (appId, { signal } = {}) => {
  const response = await api.get(`/dashboard/apps/${appId}/record-views`, { signal });
  return response.data;
};

// ── Record Views (Phase Z.7/1: uuid-keyed under /apps/:appId/) ──

// includeFields: ask the server to send this view's workflow version's field
// catalog with the view (?include=fields), so the editor doesn't need a
// second, serially-dependent request for it. Only the editor routes pass it —
// callers that just want the view row (numeric-id screen flows) must not.
export const fetchRecordViewByUUID = async (appId, rvUid, { signal, includeFields = false } = {}) => {
  const response = await api.get(`/apps/${appId}/record-views/${rvUid}`, {
    signal,
    ...(includeFields ? { params: { include: 'fields' } } : {}),
  });
  return absorbAllDataFields(response.data);
};

export const createRecordView = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/record-views`, data);
  return response.data;
};

export const updateRecordViewByUUID = async (appId, rvUid, data, options = {}) => {
  const body = {
    ...data,
    ...(options.allowedDeleteFieldKeys?.length ? { allowed_delete_field_keys: options.allowedDeleteFieldKeys } : {}),
    ...(options.ackCrossVersionDrops ? { acknowledge_cross_version_field_drops: true } : {}),
  };
  const response = await api.put(`/apps/${appId}/record-views/${rvUid}`, body);
  return response.data;
};

export const deleteRecordViewByUUID = async (appId, rvUid) => {
  const response = await api.delete(`/apps/${appId}/record-views/${rvUid}`);
  return response.data;
};

// Pre-fetch the list of screens that bind to this RV so the delete modal
// can show "where it's used" before the user clicks Delete. Same shape
// as the DELETE-409 conflict response — { dependents: [{id, uuid, name, device}] }.
export const fetchRecordViewUsage = async (appId, rvUid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/record-views/${rvUid}/usage`, { signal });
  return response.data;
};

export const fetchRecordViewFieldImpactByUUID = async (appId, rvUid, fieldId) => {
  const response = await api.get(`/apps/${appId}/record-views/${rvUid}/fields/${fieldId}/impact`);
  return response.data;
};

// Legacy-signature shim. View rows still carry rv_template_id as an integer
// column FK in layout JSONB (button job params, tile settings, chart settings,
// conditional layouts). After Z.7/1 deletes /record-views/:rvId, this helper
// resolves rv_uid via the per-app list and forwards to the uuid endpoint.
// Same pattern as fetchRDBMSTemplate from Z.4/3.
export const fetchRecordView = async (appId, rvId, { signal } = {}) => {
  // Fetch one record view by its integer PK. The by-id endpoint resolves the
  // owning app internally (authz-scoped) and returns the same enriched payload
  // as the uuid endpoint, so there's no need to list every view to translate
  // the id — and rv_id is unambiguous across apps.
  const response = await api.get(`/record-views/${rvId}`, { signal });
  return response.data;
};

// ─── HTTP Proxy (for template editor test runs) ─────────────────────────────
export const testHttpRequest = async ({ method, url, headers, body }) => {
  const response = await api.post("/proxy/test-http", {
    method,
    url,
    headers,
    body,
  });
  return response.data;
};

// ─── Platform Settings (super-admin only) ───────────────────────────────────
export const fetchPlatformSettings = async () => {
  const response = await api.get("/platform/settings");
  return response.data;
};

export const updateDeploymentsBlocked = async ({ blocked, reason }) => {
  const response = await api.put("/platform/settings/deployments-blocked", {
    blocked,
    reason: reason || "",
  });
  return response.data;
};

// ─── Branch CRUD + list ─────────────────────────────────────────────────────
// listBranches was 4 separate inline api.get calls (BranchesPage, Header's
// branch switcher, AppsTabContent's branch picker, coding-agent's
// OpenSessionModal) all hitting the same URL with no shared source of truth
// for the response shape. createBranch/deleteBranch/getBranchTrackedEdits
// were inline too, only in BranchesPage — folded in here for the same reason.
export const listBranches = async (appId) => {
  const response = await api.get(`/apps/${appId}/branches`);
  return response.data || [];
};

export const createBranch = async (appId, payload) => {
  const response = await api.post(`/apps/${appId}/branches`, payload);
  return response.data;
};

export const deleteBranch = async (appId, branchUid) => {
  const response = await api.delete(`/apps/${appId}/branches/${branchUid}`);
  return response.data;
};

export const getBranchTrackedEdits = async (appId, branchUid) => {
  const response = await api.get(`/apps/${appId}/branches/${branchUid}/tracked-edits`);
  return response.data?.edits || [];
};

// GET /branch-component-types — the branch-mergeable component vocabulary
// (type/label/order), served from the backend registry so the merge UI has
// one source of truth instead of its own hardcoded copy. Not app-scoped.
export const fetchBranchComponentTypes = () =>
  api.get("/branch-component-types").then((r) => r.data?.types || []);

// ─── Merge reconciler: read-only planner + durable decision log ────────────
// MergeComparisonPage uses the decision endpoints for conflict resolution (a
// resolution is durable and survives a reload; the backend's merge and pull both
// read the same log). getMergePlan is the union-keyspace diff and is not yet what
// the page renders — that cutover replaces /changes + /conflicts.
export const getMergePlan = async (appId, branchUid, direction = "push") => {
  const response = await api.get(`/apps/${appId}/branches/${branchUid}/plan`, {
    params: { direction },
  });
  return response.data;
};

// ─── Branch write acts: merge, pull, preview ───────────────────────────────
// These were inline api.post calls in MergeComparisonPage. They live here for
// the repo convention, and because all three now carry expectedPlanHash — a
// contract detail no page should have to remember.
//
// expectedPlanHash is the plan_hash of the comparison the user was looking at.
// The backend refuses the write if a fresh plan no longer matches it, answering
// {status: 'stale_plan'} — so the user can never approve one merge and have a
// different one applied. Pass the hash straight off the plan you rendered;
// never cache or synthesise it.

export const mergeBranchToMain = async (appId, branchUid, resolutions, expectedPlanHash) => {
  const response = await api.post(`/apps/${appId}/branches/${branchUid}/merge`, {
    resolutions,
    expected_plan_hash: expectedPlanHash || "",
  });
  return response.data;
};

export const pullMainIntoBranch = async (appId, branchUid, resolutions, expectedPlanHash) => {
  const response = await api.post(`/apps/${appId}/branches/${branchUid}/update`, {
    resolutions,
    expected_plan_hash: expectedPlanHash || "",
  });
  return response.data;
};

export const previewBranchMerge = async (appId, branchUid, resolutions) => {
  const response = await api.post(`/apps/${appId}/branches/${branchUid}/merge/preview`, {
    resolutions,
  });
  return response.data;
};

export const listMergeDecisions = async (appId, branchUid) => {
  const response = await api.get(`/apps/${appId}/branches/${branchUid}/decisions`);
  return response.data;
};

// The three seen_* hashes come straight off the plan action being resolved.
// The backend recomputes them and answers 409 if either side has moved, so
// never synthesise or cache them — read them from the plan you rendered.
export const recordMergeDecision = async (appId, branchUid, payload) => {
  const response = await api.post(`/apps/${appId}/branches/${branchUid}/decisions`, {
    ...payload,
    hashes_provided: true,
  });
  return response.data;
};

export const withdrawMergeDecision = async (appId, branchUid, componentType, componentId) => {
  const response = await api.delete(
    `/apps/${appId}/branches/${branchUid}/decisions/${componentType}/${encodeURIComponent(componentId)}`
  );
  return response.data;
};

// ─── Merge run log: what was written to main, and what it looked like ──────
// App-scoped, not branch-scoped: a run outlives its branch (the shadow app is
// deleted the moment a merge succeeds), and the question these answer is "what
// has ever landed on THIS app". Read-only — runs are written by the merge
// itself, inside the transaction that changes main.

export const listMergeRuns = async (appId, { branchUid, limit } = {}) => {
  const response = await api.get(`/apps/${appId}/merge-runs`, {
    params: { branch_uid: branchUid, limit },
  });
  return response.data;
};

// The run plus one row per component, WITHOUT the stored config bodies — a run
// can hold a lot of JSON and the list view never shows it.
export const getMergeRun = async (appId, runId) => {
  const response = await api.get(`/apps/${appId}/merge-runs/${runId}`);
  return response.data;
};

// One component's stored pair: main's config before the merge, and the branch
// config that replaced it. Shaped {main, branch} so it feeds SplitDiffView
// unchanged — the same viewer the live merge comparison uses.
export const getMergeRunComponent = async (appId, runId, componentType, componentId) => {
  const response = await api.get(
    `/apps/${appId}/merge-runs/${runId}/components/${componentType}/${encodeURIComponent(componentId)}`
  );
  return response.data;
};

// "Which merge last changed this component, and from which branch" — the
// backtracking read, starting from a component instead of a run.
export const fetchComponentMergeHistory = async (appId, componentType, componentId, limit) => {
  const response = await api.get(
    `/apps/${appId}/component-history/${componentType}/${encodeURIComponent(componentId)}`,
    { params: { limit } }
  );
  return response.data;
};

// ─── Re-exports from domain modules (Tier 2 #9a, refactor) ───────────────────
// Functions live in dedicated files for maintainability; the barrel keeps the
// 165+ existing `import { ... } from '../services/api'` callers working with
// zero changes. Sub-modules `import api from './api'` for the axios instance —
// the resulting cycle is benign because function bodies only dereference the
// import at call time, by which point api.js has finished evaluating.
export * from "./themesApi";
export * from "./customDomainsApi";
export * from "./deployApi";
export * from "./filesApi";
export * from "./screensApi";
export * from "./viewsApi";
export * from "./integrationsApi";
export * from "./usersApi";
export * from "./contextApi";
export * from "./rbacApi";
export * from "./configApi";
export * from "./appResourcesApi";
export * from "./workflowApi";
export * from "./channelsApi";
export * from "./aiCostApi";
export default api;

// ---- Deployed AI-employee tier (read-only) ----

// The employees this environment actually RUNS: rows in
// aiemployee.tbl_appconfig_ai_employees, plus `draft_only` naming any design
// rows with no deployed twin (listed by the ordinary AI Employees page exactly
// like the ones that run, which is what makes this endpoint necessary).
export const fetchDeployedAIEmployees = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/deployed-ai-employees`);
  return response.data;
};

// One deployed employee's dossier, org-scoped: the appconfig row, its granted
// agentic tools resolved against the deployed tool twin, and the live access
// context. Same payload shape as the app-scoped fetchDeployedAIEmployee.
export const fetchDeployedAIEmployeeDetail = async (orgId, userId) => {
  const response = await api.get(`/orgs/${orgId}/deployed-ai-employees/${userId}`);
  return response.data;
};